import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useI18n } from '../i18n';
import { useAuth } from '../auth/AuthContext';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { PLACY_ICON_SRC } from '../components/OffizzBrandMark';
import { getStoredProfileDetailsHints } from '../utils/profileDetailsStorage';
import {
  isAssistantSpeechSupported,
  primeAssistantSpeech,
  readAssistantSpeakEnabled,
  speakAssistantReply,
  stopAssistantSpeech,
  unlockAssistantSpeech,
  writeAssistantSpeakEnabled,
} from './assistantSpeech';
import {
  formatAssistantOpenGreeting,
  resolveAssistantSpeakFirstName,
} from './assistantVoiceLines';
import {
  consumeAssistantGreeting,
  shouldSpeakAssistantGreeting,
} from './assistantVoiceSession';
import { useSpeechRecognition } from './useSpeechRecognition';
import { assistantVoiceErrorCopy } from './voiceErrorMessage';
import type { AssistantChatMessage, PendingBookConfirm } from './useAssistantController';
import styles from './AssistantPanel.module.css';

type AssistantPanelProps = {
  open: boolean;
  onClose: () => void;
  messages: AssistantChatMessage[];
  thinking?: boolean;
  pendingBook: PendingBookConfirm | null;
  onSubmitCommand: (text: string) => void;
  onNewSearch: () => void;
  onClear: () => void;
  onConfirmBook: () => void;
  onCancelBook: () => void;
};

export function AssistantPanel({
  open,
  onClose,
  messages,
  thinking = false,
  pendingBook,
  onSubmitCommand,
  onNewSearch,
  onClear,
  onConfirmBook,
  onCancelBook,
}: AssistantPanelProps) {
  const { t, language } = useI18n();
  const { userName } = useAuth();
  const inputId = useId();
  const [draft, setDraft] = useState('');
  const [speakEnabled, setSpeakEnabled] = useState(() => readAssistantSpeakEnabled());
  const speechSupported = isAssistantSpeechSupported();
  const speech = useSpeechRecognition(language);
  const wasListeningRef = useRef(false);
  const submittedVoiceRef = useRef<string | null>(null);
  const lastSpokenIdRef = useRef<string | null>(null);
  const didGreetOpenRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const firstName = useMemo(() => {
    const hints = getStoredProfileDetailsHints(getStoredLoginIdentifier());
    return resolveAssistantSpeakFirstName(hints.firstName, userName);
  }, [userName, open]);

  const openGreeting = useMemo(
    () => formatAssistantOpenGreeting(firstName, t),
    [firstName, t],
  );

  const speakGreetingIfNeeded = useCallback(() => {
    if (!speakEnabled || !speechSupported) {
      return false;
    }
    if (!shouldSpeakAssistantGreeting()) {
      return false;
    }
    unlockAssistantSpeech();
    speakAssistantReply(openGreeting, language, { force: true, enqueue: true });
    consumeAssistantGreeting();
    return true;
  }, [speakEnabled, speechSupported, openGreeting, language]);

  const submit = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }
      // Refresh Chrome desktop autoplay unlock on each send/voice submit.
      unlockAssistantSpeech();
      // Action line + found-count are spoken from assistant bubbles (not a raw query echo).
      onSubmitCommand(trimmed);
      setDraft('');
      speech.clearTranscript();
    },
    [onSubmitCommand, speech],
  );

  const listening = speech.state === 'listening' || speech.state === 'processing';
  const errorCopy = speech.errorCode ? assistantVoiceErrorCopy(speech.errorCode) : null;

  useLayoutEffect(() => {
    const end = messagesEndRef.current;
    const scroller = end?.parentElement;
    if (!scroller) {
      return;
    }
    scroller.scrollTop = scroller.scrollHeight;
  }, [messages, thinking]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const root = backdropRef.current;
    const viewport = window.visualViewport;
    const syncVisualViewport = () => {
      if (!root) {
        return;
      }
      const top = viewport ? Math.max(0, viewport.offsetTop) : 0;
      const bottom = viewport
        ? Math.max(0, window.innerHeight - viewport.offsetTop - viewport.height)
        : 0;
      root.style.setProperty('--assistant-vv-top', `${top}px`);
      root.style.setProperty('--assistant-vv-bottom', `${bottom}px`);
    };
    syncVisualViewport();
    viewport?.addEventListener('resize', syncVisualViewport);
    viewport?.addEventListener('scroll', syncVisualViewport);
    window.addEventListener('resize', syncVisualViewport);
    return () => {
      viewport?.removeEventListener('resize', syncVisualViewport);
      viewport?.removeEventListener('scroll', syncVisualViewport);
      window.removeEventListener('resize', syncVisualViewport);
    };
  }, [open]);

  useEffect(() => {
    if (listening) {
      wasListeningRef.current = true;
      submittedVoiceRef.current = null;
      stopAssistantSpeech();
    }
  }, [listening]);

  useEffect(() => {
    if (!open) {
      stopAssistantSpeech();
      didGreetOpenRef.current = false;
      return;
    }
    primeAssistantSpeech();
    if (didGreetOpenRef.current) {
      return;
    }
    didGreetOpenRef.current = true;
    // Only after Clear/New search, or ≥1 hour since last assistant activity.
    speakGreetingIfNeeded();
  }, [open, speakGreetingIfNeeded]);

  useEffect(() => {
    if (!open) {
      return;
    }
    // Speak while thinking so the “searching for you…” line plays before the hit count.
    if (!speakEnabled || listening) {
      return;
    }
    // Speaks each new assistant bubble once (action line, then נמצאו N נכסים).
    const latest = [...messages].reverse().find((message) => message.role === 'assistant' && !message.archived);
    if (!latest || latest.id === lastSpokenIdRef.current) {
      return;
    }
    lastSpokenIdRef.current = latest.id;
    speakAssistantReply(latest.text, language, { force: true, enqueue: true });
  }, [open, speakEnabled, listening, messages, language]);

  useEffect(() => {
    const text = speech.transcript.trim();
    if (!wasListeningRef.current || speech.state !== 'idle' || !text) {
      return;
    }
    if (submittedVoiceRef.current === text) {
      return;
    }
    submittedVoiceRef.current = text;
    wasListeningRef.current = false;
    submit(text);
  }, [speech.state, speech.transcript, submit]);

  const toggleSpeak = useCallback(() => {
    if (!speechSupported) {
      return;
    }
    unlockAssistantSpeech();
    setSpeakEnabled((prev) => {
      const next = !prev;
      writeAssistantSpeakEnabled(next);
      if (!next) {
        stopAssistantSpeech();
      }
      return next;
    });
  }, [speechSupported]);

  if (!open) {
    return null;
  }

  return (
    <div ref={backdropRef} className={styles.backdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${inputId}-title`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.titleRow}>
            <img
              src={PLACY_ICON_SRC}
              alt=""
              className={styles.titleLogo}
              draggable={false}
            />
            <h2 id={`${inputId}-title`} className={styles.title}>
              {t('assistant.panel.title')}
            </h2>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={`${styles.speakToggle}${speakEnabled ? ` ${styles.speakToggleOn}` : ''}`}
              aria-pressed={speakEnabled}
              aria-label={
                !speechSupported
                  ? t('assistant.panel.speakUnsupported')
                  : speakEnabled
                    ? t('assistant.panel.speakOff')
                    : t('assistant.panel.speakOn')
              }
              title={
                !speechSupported
                  ? t('assistant.panel.speakUnsupported')
                  : speakEnabled
                    ? t('assistant.panel.speakOff')
                    : t('assistant.panel.speakOn')
              }
              disabled={!speechSupported}
              onClick={(event) => {
                event.stopPropagation();
                toggleSpeak();
              }}
            >
              <SpeakerIcon muted={!speakEnabled} />
            </button>
            <button
              type="button"
              className={styles.newSearch}
              aria-label={t('assistant.panel.newSearch')}
              title={t('assistant.panel.newSearch')}
              onClick={(event) => {
                event.stopPropagation();
                if (listening) {
                  speech.stop();
                }
                stopAssistantSpeech();
                setDraft('');
                speech.clearTranscript();
                onNewSearch();
                // Clear/new search re-arms greeting for this session.
                didGreetOpenRef.current = false;
                speakGreetingIfNeeded();
                didGreetOpenRef.current = true;
              }}
            >
              {t('assistant.panel.newSearch')}
            </button>
            <button
              type="button"
              className={styles.clearChat}
              aria-label={t('assistant.panel.clear')}
              title={t('assistant.panel.clear')}
              disabled={messages.length === 0}
              onClick={(event) => {
                event.stopPropagation();
                if (listening) {
                  speech.stop();
                }
                stopAssistantSpeech();
                setDraft('');
                speech.clearTranscript();
                onClear();
                didGreetOpenRef.current = false;
                speakGreetingIfNeeded();
                didGreetOpenRef.current = true;
              }}
            >
              {t('assistant.panel.clear')}
            </button>
            <button
              type="button"
              className={styles.close}
              onClick={() => {
                stopAssistantSpeech();
                onClose();
              }}
              aria-label={t('assistant.panel.close')}
            >
              ×
            </button>
          </div>
        </header>

        <div className={styles.messages} aria-live="polite">
          {messages.length === 0 && !thinking ? (
            <p className={styles.empty}>{openGreeting}</p>
          ) : (
            <>
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={[
                    message.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant,
                    message.archived ? styles.bubbleArchived : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {message.text}
                </div>
              ))}
              {thinking ? (
                <div
                  className={`${styles.bubbleAssistant} ${styles.bubbleThinking}`}
                  role="status"
                  aria-label={t('assistant.panel.thinking')}
                >
                  <span className={styles.thinkingDots} aria-hidden>
                    <span />
                    <span />
                    <span />
                  </span>
                  {t('assistant.panel.thinking')}
                </div>
              ) : null}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {pendingBook ? (
          <div className={styles.confirmBox} role="group" aria-label={t('assistant.book.confirmAria')}>
            <p className={styles.confirmText}>{t('assistant.book.confirmText')}</p>
            <div className={styles.confirmActions}>
              <button type="button" className={styles.confirmPrimary} onClick={onConfirmBook}>
                {t('assistant.book.confirm')}
              </button>
              <button type="button" className={styles.confirmSecondary} onClick={onCancelBook}>
                {t('assistant.book.cancel')}
              </button>
            </div>
          </div>
        ) : null}

        <form
          className={styles.composer}
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            submit(draft);
          }}
        >
          <label className={styles.srOnly} htmlFor={inputId}>
            {t('assistant.panel.inputLabel')}
          </label>
          <input
            id={inputId}
            className={styles.input}
            value={listening ? speech.interimTranscript || draft : draft}
            onChange={(event) => setDraft(event.target.value)}
            autoComplete="off"
          />
          <button
            type="button"
            className={`${styles.mic}${listening ? ` ${styles.micActive}` : ''}`}
            aria-label={listening ? t('assistant.voice.stop') : t('assistant.voice.start')}
            disabled={speech.backend === 'unavailable'}
            onClick={() => {
              unlockAssistantSpeech();
              if (listening) {
                speech.stop();
              } else {
                void speech.start();
              }
            }}
          >
            <MicIcon />
          </button>
          <button
            type="submit"
            className={styles.send}
            onClick={() => {
              unlockAssistantSpeech();
            }}
          >
            {t('assistant.panel.send')}
          </button>
        </form>
        {errorCopy ? (
          <p className={styles.error} role="alert">
            {t(errorCopy.key, errorCopy.params)}
          </p>
        ) : null}
        {speech.backend === 'unavailable' ? (
          <p className={styles.hint}>{t('assistant.voice.unsupported')}</p>
        ) : null}
      </div>
    </div>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden fill="currentColor">
      <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" />
    </svg>
  );
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden fill="currentColor">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
      {muted ? (
        <path d="M19.1 4.9 4.9 19.1l1.4 1.4L20.5 6.3 19.1 4.9z" />
      ) : (
        <path d="M14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
      )}
    </svg>
  );
}
